import { type ReactNode } from 'react';
import { FormulaDisplay } from '@/components/Modals/EditDataTableFormulaColumn/FormulaDisplay';
import { capitalise } from '@/helpers';
import { _isNotBlank } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import type { DataTableColumn } from '@/components/UI/DataTableV2/DataTable.model';
import type { DataTableSampleColumnV1 } from '@/model/DataTable.model';

/**
 * Builds the tooltip content shown when hovering a column type icon in the data
 * table headers. Mirrors the label rendered inside the header so hovering an icon
 * reveals the full column title even when the header text is truncated.
 */
export const renderColumnTypeSymbolTooltip = (
  column: Readonly<DataTableColumn> | undefined,
  columns: ReadonlyArray<DataTableColumn>
): ReactNode => {
  switch (column?.type) {
    case 'formula':
      return <FormulaDisplay formula={column?.formula} columns={columns} />;
    case 'timestampBaseline':
      return 'Timestamp: PK Dose';
    case 'timestampBaselineRelative':
      return 'Timestamp: PK Bleed';
    case 'sampleCollectedAt':
      return 'Collected At';
    case 'sampleDoseTime':
      return 'Dosed At';
    case 'samplePlannedAt':
      return 'Planned At';
    case 'sampleId':
      return 'ID';
    case 'sampleStatus':
      return 'Status';
    case 'sampleDetail':
      return column?.name;
    case 'sampleComments':
      return 'Comments';
    case 'sample':
      return sampleColumnTooltip(column);
    default:
      return capitalise(column?.type);
  }
};

/**
 * Builds the tooltip content shown when hovering a parent (group) column's type
 * icon. Sample groups are described by their sample metadata; other groups mirror
 * the visible group header title (`type :: name`).
 */
export const renderParentColumnTypeSymbolTooltip = (parentColumn: Readonly<DataTableColumn>): ReactNode => {
  if (parentColumn.type === 'sample') {
    return sampleColumnTooltip(parentColumn);
  }
  return `${parentColumn.type} :: ${parentColumn.name}`;
};

/**
 * Composes a sample column's tooltip title from its sample metadata
 * (sample type, planned group and, when present, the planned timepoint).
 */
export const sampleColumnTooltip = (column: Readonly<DataTableSampleColumnV1>): string => {
  const timepoint = _isNotBlank(column.sample.planned_sample_timepoint)
    ? DateUtils.renderDuration(column.sample.planned_sample_timepoint, {
        format: 'short',
        zeros: true,
        defaultResponse: '',
      })
    : '';

  return [column.sample.sample_type_name, column.sample.planned_sample_group, timepoint]
    .filter(_isNotBlank)
    .join(' · ');
};
